import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { klienApi } from '../api/klien';
import { perusahaanApi } from '../api/perusahaan';
import { ApiException } from '../api/client';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { Card, AccentCard } from '../components/Card';
import { AppLayout } from '../components/Layout';
import type { KlienMeResponse, PerusahaanSummary } from '../types/api';

export function DashboardPage() {
  const [me, setMe] = useState<KlienMeResponse | null>(null);
  const [perusahaanList, setPerusahaanList] = useState<PerusahaanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        const [meData, listData] = await Promise.all([klienApi.me(), perusahaanApi.mine()]);
        setMe(meData);
        setPerusahaanList(listData);
      } catch (err) {
        setError(err instanceof ApiException ? err.message : 'Gagal memuat data.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const aktif = perusahaanList.filter((p) => p.status === 'Aktif').length;
  const menunggu = perusahaanList.filter((p) => p.status === 'MenungguApproval').length;

  return (
    <AppLayout>
      <AccentCard
        title={me ? `Selamat datang, ${me.namaLengkap}` : 'Dashboard'}
        subtitle="Ringkasan akun keanggotaan Booku Client Anda"
      >
        {error && <Alert variant="error">{error}</Alert>}
      </AccentCard>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <StatCard label="Total Perusahaan" value={loading ? '—' : String(perusahaanList.length)} />
        <StatCard label="Aktif" value={loading ? '—' : String(aktif)} accent="green" />
        <StatCard
          label="Menunggu Approval"
          value={loading ? '—' : String(menunggu)}
          accent={menunggu > 0 ? 'yellow' : undefined}
        />
      </div>

      <div className="mt-6">
        <Card title="Aksi Cepat">
          <div className="flex flex-wrap gap-3">
            <Link to="/aplikasi/booku">
              <Button variant="primary">Buka Aplikasi Booku</Button>
            </Link>
            <Link to="/perusahaan/daftar">
              <Button variant="outline">+ Daftarkan Perusahaan Baru</Button>
            </Link>
            <Link to="/profil">
              <Button variant="outline">Profil Saya</Button>
            </Link>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'green' | 'yellow';
}) {
  const accentColor =
    accent === 'green'
      ? 'text-green-700'
      : accent === 'yellow'
      ? 'text-yellow-700'
      : 'text-gray-800';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`mt-2 text-3xl font-semibold ${accentColor}`}>{value}</div>
    </div>
  );
}
